/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid'
import { ColumnDef } from '@tanstack/react-table'
import moment from 'moment'
import ProgressBar from 'app/components/ProgressBar'
import { CallsModal } from 'app/components/scorecards/CallsModal'
import { MarketingContactTable } from 'app/components/shared/MarketingContactTable'
import { Stats } from 'app/components/Stats'
import MetricCell from 'app/components/table/MetricCell'
import type { ScorecardJson } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { LocationsRow, StaffRow } from 'app/types/scorecards'
import { staffMetricColumns } from 'app/table-columns/shared/staff-metric-columns'

const getDate = (date: string, dateRange: NonNullable<ScorecardJson[`dateRange`]>) => {
  const parsedDate = moment.utc(date)
  switch (dateRange) {
    case `today`:
      return parsedDate.format(`MMM D, YYYY`)
    case `this_week`:
      return parsedDate.format(`[Week of] MMM D, YYYY`)
    case `this_month`:
      return parsedDate.format(`MMM YYYY`)
    case `this_quarter`:
      return parsedDate.format(`[Q]Q YYYY`)
    case `this_year`:
      return parsedDate.format(`YYYY`)
    default:
      return ``
  }
}

export const locationColumns = (
  dateRange: NonNullable<ScorecardJson[`dateRange`]>,
  isSMC = false,
): ColumnDef<LocationsRow, any>[] => [
  {
    header: `Location`,
    id: `Location`,
    accessorFn: (row) => row.bucket,
    cell: ({ row, getValue }) => (
      <div
        onClick={row.getCanExpand() ? row.getToggleExpandedHandler() : undefined}
        style={{
          // Since rows are flattened by default,
          // we can use the row.depth property
          // and paddingLeft to visually indicate the depth
          // of the row
          paddingLeft: `${row.depth * 1.5}rem`,
        }}
        className={row.getCanExpand() ? `cursor-pointer` : ``}
      >
        <div className="flex items-center">
          {row.getCanExpand() && (
            <button
              className="mr-2"
              {...{
                // onClick: row.getToggleExpandedHandler(),
                // style: { cursor: `pointer` },
              }}
            >
              {row.getIsExpanded() ? <ChevronUpIcon height={18} /> : <ChevronDownIcon height={18} />}
            </button>
          )}
          {` `}
          {row.depth === 0 ? (
            <span className="text-ui-md">{getValue()}</span>
          ) : (
            <span className="text-ui-xs">{getDate(row.original.date, dateRange)}</span>
          )}
        </div>
      </div>
    ),
    meta: {
      group: `General`,
      defaultVisible: true,
      pinned: true,
    },
    enableHiding: false,
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorKey: `leads`,
    cell: (props) => <MetricCell value={props.row.original.leads} />,
    accessorFn: (row) => row.leads,
    header: `Leads`,
    id: `Leads`,
    meta: {
      onClick: (row) => {
        showModal(
          <MarketingContactTable
            title="Leads"
            filterContactsBy="Leads"
            subtitle={row.bucket}
            fromDate={row.date}
            toDate={inferToDate(row.date, dateRange)}
            locationIds={[row.id]}
            showDisqualified={true}
            onClose={closeModal}
          />,
          {
            hideTitle: true,
            removePadding: true,
            isFullScreen: true,
            useRadix: true,
          },
        )
      },
      group: `Totals`,
      defaultVisible: true,
      infoTooltip: {
        title: `Leads`,
        message: `All contacts that have a became a lead date within the date range. This includes both qualified and disqualified leads.`,
      },
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorKey: `MQLs`,
    cell: (props) => <MetricCell value={props.row.original.MQLs} />,
    accessorFn: (row) => row.MQLs,
    header: `Qualified Leads`,
    id: `Qualified Leads`,
    meta: {
      onClick: (row) => {
        showModal(
          <MarketingContactTable
            title="Qualified Leads"
            filterContactsBy="Qualified Leads"
            fromDate={row.date}
            toDate={inferToDate(row.date, dateRange)}
            locationIds={[row.id]}
            subtitle={row.bucket}
            onClose={closeModal}
          />,
          {
            hideTitle: true,
            removePadding: true,
            isFullScreen: true,
            useRadix: true,
          },
        )
      },
      group: `Totals`,
      defaultVisible: true,
      infoTooltip: {
        title: `Qualified Leads`,
        message: `All contacts with a became a lead date within the date range who does not have a disqualifying closed lost reason.`,
      },
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorKey: `totalSpend`,
    cell: (props) => <MetricCell value={props.row.original.totalSpend} type="dollars" />,
    accessorFn: (row) => row.totalSpend,
    header: `Spend`,
    id: `Spend`,
    meta: {
      group: `Financial`,
      defaultVisible: true,
      infoTooltip: {
        title: `Spend`,
        message: `Total Facebook spend + Google spend within the date range.`,
      },
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorFn: (row) => row.production + row.scheduledProduction,
    id: `Production`,
    header: `Net Production`,
    meta: {
      group: `Financial`,
      defaultVisible: true,
      infoTooltip: {
        title: `Net Production`,
        message: `The total production (completed and scheduled) after write offs and adjustments for contacts with a became a lead date within the date range.`,
      },
      ControlledComponent: (row) => (
        <Stats
          title="Net Production"
          count={row!.production + row!.scheduledProduction}
          unitType="dollars"
          breakdown={[
            { name: `Completed Production`, count: row!.production },
            { name: `Scheduled Production`, count: row!.scheduledProduction },
          ]}
        >
          <MetricCell value={row!.production + row!.scheduledProduction} type="dollars" />
        </Stats>
      ),
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorKey: `ROI`,
    cell: (props) => <MetricCell value={props.row.original.ROI} type="percentage" />,
    accessorFn: (row) => row.ROI,
    header: `ROI`,
    id: `ROI`,
    meta: {
      group: `Financial`,
      defaultVisible: true,
      infoTooltip: {
        title: `ROI`,
        message: `Return on investment: net production divided by ${isSMC ? `budget` : `spend`}.`,
      },
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorKey: `appointments`,
    cell: (props) => <MetricCell value={props.row.original.appointments} />,
    accessorFn: (row) => row.appointments,
    header: `Appointments Scheduled`,
    id: `Appointments Scheduled`,
    meta: {
      onClick: (row) => {
        showModal(
          <MarketingContactTable
            filterContactsBy="Appointments"
            title="Appointments Scheduled"
            subtitle={row.bucket}
            fromDate={row.date}
            toDate={inferToDate(row.date, dateRange)}
            locationIds={[row.id]}
            onClose={closeModal}
          />,
          {
            hideTitle: true,
            removePadding: true,
            isFullScreen: true,
            useRadix: true,
          },
        )
      },
      group: `Totals`,
      defaultVisible: true,
      infoTooltip: {
        title: `Appointments Scheduled`,
        message: `All contacts who have a became a lead within the date range selected and have scheduled an appointment at any point. However this does not mean they have completed the appointment.`,
      },
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorKey: `patients`,
    cell: (props) => <MetricCell value={props.row.original.patients} />,
    accessorFn: (row) => row.patients,
    header: `New Patients`,
    id: `New Patients`,
    meta: {
      onClick: (row) => {
        showModal(
          <MarketingContactTable
            filterContactsBy="Patients"
            title="New Patients"
            subtitle={row.bucket}
            fromDate={row.date}
            toDate={inferToDate(row.date, dateRange)}
            locationIds={[row.id]}
            onClose={closeModal}
          />,
          {
            hideTitle: true,
            removePadding: true,
            isFullScreen: true,
            useRadix: true,
          },
        )
      },
      group: `Totals`,
      defaultVisible: true,
      infoTooltip: {
        title: `New Patients`,
        message: `All contacts who have have a became a lead date within the date range and have completed a first appointment.`,
      },
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorKey: `CPL`,
    cell: (props) => <MetricCell value={props.row.original.CPL} type="dollars" />,
    accessorFn: (row) => row.CPL,
    header: `CPL`,
    id: `CPL`,
    meta: {
      group: `Calculations`,
      defaultVisible: false,
      infoTooltip: {
        title: `CPL`,
        message: `Total ${
          isSMC ? `retainer or budget` : `spend`
        } divided by number of contacts with a became a lead date from the selected channels and within the date range.`,
      },
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorKey: `CPQL`,
    cell: (props) => <MetricCell value={props.row.original.CPQL} type="dollars" />,
    accessorFn: (row) => row.CPQL,
    header: `CPQL`,
    id: `CPQL`,
    meta: {
      group: `Calculations`,
      defaultVisible: false,
      infoTooltip: {
        title: `CPQL`,
        message: `Total ${
          isSMC ? `retainer or budget` : `spend`
        } divided by number of qualified contacts with a became a lead date from the selected channels and within the date range.`,
      },
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorKey: `CPA`,
    cell: (props) => <MetricCell value={props.row.original.CPA} type="dollars" />,
    accessorFn: (row) => row.CPA,
    header: `CPA`,
    id: `CPA`,
    meta: {
      group: `Calculations`,
      defaultVisible: false,
      infoTooltip: {
        title: `CPA`,
        message: `Total ${
          isSMC ? `retainer or budget` : `spend`
        } divided by number of appointments generated from the selected channels and within the date range. This contains a count of contacts who have scheduled an appointment regardless of whether it has been completed or not.`,
      },
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorKey: `CAC`,
    cell: (props) => <MetricCell value={props.row.original.CAC} type="dollars" />,
    accessorFn: (row) => row.CAC,
    header: `CAC`,
    id: `CAC`,
    meta: {
      group: `Calculations`,
      defaultVisible: false,
      infoTooltip: {
        title: `CAC`,
        message: `Total ${
          isSMC ? `retainer or budget` : `spend`
        } divided by contacts who completed their first appointment from the selected channels and within the date range.`,
      },
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorFn: (row) => row.scheduleRate,
    accessorKey: `scheduleRate`,
    header: `Scheduled Rate`,
    id: `Scheduled Rate`,
    meta: {
      group: `Calculations`,
      defaultVisible: true,
      infoTooltip: {
        title: `Scheduled Rate`,
        message: `Percentage of qualified leads that scheduled a first appointment within the date range.`,
      },
      ControlledComponent: (row) => (
        <div className="flex items-center gap-x-3 px-2">
          <ProgressBar progress={row!.scheduleRate} />
          <span className="text-body-xs">
            <MetricCell value={row!.scheduleRate} type="percentage" />
          </span>
        </div>
      ),
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorKey: `leadsNeedsAction`,
    accessorFn: (row) => row.leadsNeedsAction,
    cell: (props) => <MetricCell value={props.getValue()} />,
    header: `Needs Action`,
    id: `Needs Action Leads`,
    meta: {
      onClick: (row) => {
        showModal(
          <MarketingContactTable
            filterContactsBy="Needs Action"
            title="Needs Action"
            subtitle={row.bucket}
            fromDate={row.date}
            toDate={inferToDate(row.date, dateRange)}
            locationIds={[row.id]}
            onClose={closeModal}
          />,
          {
            hideTitle: true,
            removePadding: true,
            isFullScreen: true,
            useRadix: true,
          },
        )
      },
      group: `Lead Status`,
      defaultVisible: true,
      infoTooltip: {
        title: `Needs Action Leads`,
        message: `All contacts who have a lead status of “Needs Action” and a became a lead date within the date range. A needs action describes a contact who requires further action from the office administrative team.`,
      },
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorKey: `leadsInProgress`,
    accessorFn: (row) => row.leadsInProgress,
    cell: (props) => <MetricCell value={props.getValue()} />,
    header: `In Progress`,
    id: `In Progress Leads`,
    meta: {
      onClick: (row) => {
        showModal(
          <MarketingContactTable
            filterContactsBy="In Progress"
            title="In Progress"
            subtitle={row.bucket}
            fromDate={row.date}
            toDate={inferToDate(row.date, dateRange)}
            locationIds={[row.id]}
            onClose={closeModal}
          />,
          {
            hideTitle: true,
            removePadding: true,
            isFullScreen: true,
            useRadix: true,
          },
        )
      },
      group: `Lead Status`,
      defaultVisible: true,
      infoTooltip: {
        title: `In Progress Leads`,
        message: `All contacts who have a lead status of “In Progress” and a became a lead date within the date range. An in progress lead status describes a contact where the office administrative team is actively taking action to schedule.`,
      },
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorKey: `leadsScheduled`,
    accessorFn: (row) => row.leadsScheduled,
    cell: (props) => <MetricCell value={props.getValue()} />,
    header: `Scheduled Leads`,
    id: `Scheduled Leads`,
    meta: {
      onClick: (row) => {
        showModal(
          <MarketingContactTable
            filterContactsBy="Scheduled"
            title="Scheduled Leads"
            subtitle={row.bucket}
            fromDate={row.date}
            toDate={inferToDate(row.date, dateRange)}
            locationIds={[row.id]}
            onClose={closeModal}
          />,
          {
            hideTitle: true,
            removePadding: true,
            isFullScreen: true,
            useRadix: true,
          },
        )
      },
      group: `Lead Status`,
      defaultVisible: true,
      infoTooltip: {
        title: `Scheduled Leads`,
        message: `All contacts who have a lead status of “Scheduled” and a became a lead date within the date range. A scheduled lead status describes a contact who has a scheduled first appointment in the future.`,
      },
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorKey: `leadsDisqualified`,
    accessorFn: (row) => row.leadsDisqualified,
    cell: (props) => <MetricCell value={props.getValue()} />,
    header: `Disqualified Leads`,
    id: `Disqualified Leads`,
    meta: {
      onClick: (row) => {
        showModal(
          <MarketingContactTable
            filterContactsBy="Disqualified Leads"
            title="Disqualified Leads"
            subtitle={row.bucket}
            fromDate={row.date}
            toDate={inferToDate(row.date, dateRange)}
            locationIds={[row.id]}
            onClose={closeModal}
          />,
          {
            hideTitle: true,
            removePadding: true,
            isFullScreen: true,
            useRadix: true,
          },
        )
      },
      group: `Totals`,
      defaultVisible: false,
      infoTooltip: {
        title: `Disqualified Leads`,
        message: `All contacts with a became a lead date within the date range who has a disqualifying closed lost reason.`,
      },
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorKey: `leadsClosedWon`,
    accessorFn: (row) => row.leadsClosedWon,
    cell: (props) => <MetricCell value={props.getValue()} />,
    header: `Closed Won`,
    id: `Closed Won Leads`,
    meta: {
      onClick: (row) => {
        showModal(
          <MarketingContactTable
            filterContactsBy="Closed Won"
            title="Closed Won"
            subtitle={row.bucket}
            fromDate={row.date}
            toDate={inferToDate(row.date, dateRange)}
            locationIds={[row.id]}
            onClose={closeModal}
          />,
          {
            hideTitle: true,
            removePadding: true,
            isFullScreen: true,
            useRadix: true,
          },
        )
      },
      group: `Lead Status`,
      defaultVisible: false,
      infoTooltip: {
        title: `Closed Won Leads`,
        message: `All contacts who have a lead status of “Closed Won” and a became a lead date within the date range. A closed won lead status describes a contact who has completed a first appointment.`,
      },
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorKey: `leadsClosedLost`,
    accessorFn: (row) => row.leadsClosedLost,
    cell: (props) => <MetricCell value={props.getValue()} />,
    header: `Closed Lost`,
    id: `Closed Lost Leads`,
    meta: {
      onClick: (row) => {
        showModal(
          <MarketingContactTable
            filterContactsBy="Closed Lost"
            title="Closed Lost"
            subtitle={row.bucket}
            fromDate={row.date}
            toDate={inferToDate(row.date, dateRange)}
            locationIds={[row.id]}
            showDisqualified={true}
            onClose={closeModal}
          />,
          {
            hideTitle: true,
            removePadding: true,
            isFullScreen: true,
            useRadix: true,
          },
        )
      },
      group: `Lead Status`,
      defaultVisible: true,
      infoTooltip: {
        title: `Closed Lost Leads`,
        message: `All contacts who have a lead status of “Closed Lost” and a became a lead date within the date range. A closed lost lead status describes a contact who is disqualified or could not be scheduled for an appointment.`,
      },
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorFn: (row) => row.leadsWithMissedCall,
    id: `Missed Calls`,
    header: `Missed Calls`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    meta: {
      onClick: (row) => {
        showModal(
          <MarketingContactTable
            filterContactsBy="Missed Calls"
            title="Missed Calls"
            subtitle={row.bucket}
            fromDate={row.date}
            toDate={inferToDate(row.date, dateRange)}
            locationIds={[row.id]}
            onClose={closeModal}
          />,
          {
            hideTitle: true,
            removePadding: true,
            isFullScreen: true,
            useRadix: true,
          },
        )
      },
      group: `Totals`,
      defaultVisible: false,
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorFn: (row) => row.qualifiedNPCalls,
    id: `Qualified NP Calls`,
    header: `Qualified NP Calls`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    meta: {
      onClick: (row) => {
        showModal(
          <CallsModal
            title="Qualified NP Calls"
            subtitle={row.bucket}
            locationId={row.id}
            type="qualified-np"
            fromDate={row.date}
            toDate={inferToDate(row.date, dateRange)}
            onClose={closeModal}
          />,
          {
            hideTitle: true,
            removePadding: true,
            isFullScreen: true,
            useRadix: true,
          },
        )
      },
      group: `Calls`,
      defaultVisible: false,
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorFn: (row) => row.scheduledNPCalls,
    id: `Scheduled NP Calls`,
    header: `Scheduled NP Calls`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    meta: {
      onClick: (row) => {
        showModal(
          <CallsModal
            title="Scheduled NP Calls"
            subtitle={row.bucket}
            locationId={row.id}
            type="scheduled-np"
            fromDate={row.date}
            toDate={inferToDate(row.date, dateRange)}
            onClose={closeModal}
          />,
          {
            hideTitle: true,
            removePadding: true,
            isFullScreen: true,
            useRadix: true,
          },
        )
      },
      group: `Calls`,
      defaultVisible: false,
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorFn: (row) => row.unscheduledNPCalls,
    id: `Unscheduled NP Calls`,
    header: `Unscheduled NP Calls`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    meta: {
      onClick: (row) => {
        showModal(
          <CallsModal
            title="Unscheduled NP Calls"
            subtitle={row.bucket}
            locationId={row.id}
            type="unscheduled-np"
            fromDate={row.date}
            toDate={inferToDate(row.date, dateRange)}
            onClose={closeModal}
          />,
          {
            hideTitle: true,
            removePadding: true,
            isFullScreen: true,
            useRadix: true,
          },
        )
      },
      group: `Calls`,
      defaultVisible: false,
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorFn: (row) => row.averageDuration,
    id: `Average Call Duration`,
    header: `Average Call Duration`,
    cell: (props) => <MetricCell value={props.getValue()} type="time" />,
    meta: {
      group: `Calls`,
      defaultVisible: false,
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorFn: (row) => row.speedToLead,
    id: `Speed to Lead`,
    header: `Speed to Lead`,
    cell: (props) => <MetricCell value={props.getValue()} type="days" />,
    meta: {
      group: `Calls`,
      defaultVisible: false,
    },
    sortingFn: `onlySortParentRows`,
  },
  {
    accessorFn: (row) => row.NPCallScheduleRate,
    id: `NP Call Schedule Rate`,
    header: `NP Call Schedule Rate`,
    cell: (props) => <MetricCell value={props.getValue()} type="percentage" />,
    meta: {
      group: `Calls`,
      defaultVisible: false,
    },
    sortingFn: `onlySortParentRows`,
  },
]

const inferToDate = (fromDate: string, dateRange: NonNullable<ScorecardJson[`dateRange`]>) => {
  const parsedDate = moment.utc(fromDate)
  switch (dateRange) {
    case `today`:
      return parsedDate.endOf(`day`).toISOString()
    case `this_week`:
      return parsedDate.endOf(`week`).toISOString()
    case `this_month`:
      return parsedDate.endOf(`month`).toISOString()
    case `this_quarter`:
      return parsedDate.endOf(`quarter`).toISOString()
    case `this_year`:
      return parsedDate.endOf(`year`).toISOString()
    default:
      return ``
  }
}

export const staffColumns = (dateRange: NonNullable<ScorecardJson[`dateRange`]>): ColumnDef<StaffRow, any>[] => [
  {
    header: `Staff`,
    id: `Staff`,
    accessorFn: (row) => row.bucket,
    cell: ({ row, getValue }) => (
      <div
        onClick={row.getCanExpand() ? row.getToggleExpandedHandler() : undefined}
        style={{
          // Since rows are flattened by default,
          // we can use the row.depth property
          // and paddingLeft to visually indicate the depth
          // of the row
          paddingLeft: `${row.depth * 1.5}rem`,
        }}
        className={row.getCanExpand() ? `cursor-pointer` : ``}
      >
        <div className="flex items-center">
          {row.getCanExpand() && (
            <button
              className="mr-2"
              {...{
                // onClick: row.getToggleExpandedHandler(),
                // style: { cursor: `pointer` },
              }}
            >
              {row.getIsExpanded() ? <ChevronUpIcon height={18} /> : <ChevronDownIcon height={18} />}
            </button>
          )}
          {` `}
          {row.depth === 0 ? (
            <span className="text-ui-md">{getValue()}</span>
          ) : (
            <span className="text-ui-xs">{getDate(row.original.date, dateRange)}</span>
          )}
        </div>
      </div>
    ),
    meta: {
      group: `General`,
      defaultVisible: true,
      pinned: true,
    },
    enableHiding: false,
    sortingFn: `onlySortParentRows`,
  },
  ...(staffMetricColumns({
    groupedRows: true,
    onCallClick: (row, type) => {
      showModal(
        <CallsModal
          title={
            type === `qualified-np`
              ? `Qualified NP Calls`
              : type === `scheduled-np`
                ? `Scheduled NP Calls`
                : `Unscheduled NP Calls`
          }
          subtitle={row.bucket}
          staffId={row.id}
          type={type as any}
          fromDate={row.date}
          toDate={inferToDate(row.date, dateRange)}
          onClose={closeModal}
        />,
        {
          hideTitle: true,
          removePadding: true,
          isFullScreen: true,
        },
      )
    },
  }) as ColumnDef<StaffRow, any>[]),
]
